import * as React from 'react';
import { Slot } from '@radix-ui/react-slot';
import { cva, type VariantProps } from 'class-variance-authority';
import { cn } from '@/lib/cn';

/**
 * Button — primitiva shadcn-style con los colores de marca ISOFT.
 * Soporta `asChild` para envolver <Link> de react-router.
 */
const buttonVariants = cva(
  'inline-flex items-center justify-center gap-2 whitespace-nowrap rounded-md text-sm font-semibold transition-all focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary/30 disabled:pointer-events-none disabled:opacity-50 active:scale-[0.98]',
  {
    variants: {
      variant: {
        default: 'bg-primary text-white shadow-sm hover:bg-primary-dark',
        secondary:
          'border border-border bg-white text-font-primary hover:bg-bg',
        outline:
          'border border-primary/30 bg-primary-subtle text-primary hover:bg-primary hover:text-white',
        ghost: 'text-font-secondary hover:bg-bg hover:text-font-primary',
        destructive: 'border border-error bg-white text-error hover:bg-red-50',
        success: 'bg-accent text-white shadow-sm hover:bg-accent/90',
        gradient:
          'brand-gradient text-white shadow-md hover:shadow-lg hover:brightness-105',
      },
      size: {
        sm: 'h-9 px-3',
        default: 'h-10 px-4',
        lg: 'h-11 px-6 text-[15px]',
        icon: 'h-10 w-10',
      },
    },
    defaultVariants: { variant: 'default', size: 'default' },
  },
);

export interface ButtonProps
  extends React.ButtonHTMLAttributes<HTMLButtonElement>,
    VariantProps<typeof buttonVariants> {
  asChild?: boolean;
}

export const Button = React.forwardRef<HTMLButtonElement, ButtonProps>(
  ({ className, variant, size, asChild = false, ...props }, ref) => {
    const Comp = asChild ? Slot : 'button';
    return (
      <Comp
        ref={ref}
        className={cn(buttonVariants({ variant, size, className }))}
        {...props}
      />
    );
  },
);
Button.displayName = 'Button';

export { buttonVariants };
